<template>
    <div class="pa-5 compact-form" v-if="activeUser">
        <v-form ref="form" v-model="valid" lazy-validation>
            <v-row no-gutters class="ma-0">
                <v-col class="mt-2 row-title" cols="3">{{ "First name" | t }}</v-col>
                <v-col cols="6">
                    <v-text-field
                        :value="activeUser.first_name"
                        @input="updateField($event, 'first_name')"
                        class="text-field-item mb-1"
                        dense
                        outlined
                    />
                </v-col>
            </v-row>
            <v-row no-gutters class="ma-0">
                <v-col class="mt-2 row-title" cols="3">{{ "Last name" | t }}</v-col>
                <v-col cols="6">
                    <v-text-field
                        :value="activeUser.last_name"
                        @input="updateField($event, 'last_name')"
                        class="text-field-item mb-1"
                        dense
                        outlined
                    />
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col class="mt-0 row-title" cols="3">{{ "Phone" | t }}</v-col>
                <v-col cols="6">
                    <v-text-field
                        :value="activeUser.phone"
                        @input="updateField($event, 'phone')"
                        class="text-field-item mb-1"
                        dense
                        outlined
                    />
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col class="col-item row-title" cols="3">{{ "Email" | t }}</v-col>
                <v-col class="col-item" cols="6">
                    <v-text-field
                        required
                        :rules="emailRules"
                        :value="activeUser.email"
                        @input="updateField($event, 'email')"
                        class="text-field-item mb-1"
                        :disabled="$route.params.userId !== 'new'"
                        dense
                        outlined
                    />
                </v-col>
            </v-row>
            <v-alert v-if="errorMessage.length > 0" type="error" color="bal-red">
                {{ errorMessage }}
            </v-alert>
            <v-row no-gutters v-if="($route.params.userId === 'new' && activeUser.status === 'active') || permittedToChangePasswords">
                <v-col class="col-item row-title" cols="3">{{ "Password" | t }}</v-col>
                <v-col class="col-item" cols="6">
                    <v-text-field
                        required
                        :rules="passwordRules"
                        :value="activeUser.password"
                        @input="updateField($event, 'password')"
                        class="text-field-item mb-1"
                        dense
                        outlined
                    />
                </v-col>
            </v-row>
            <v-divider></v-divider>
            <v-row no-gutters class="mt-5">
                <v-col class="col-item row-title" cols="3">{{ "Position" | t }}</v-col>
                <v-col class="col-item" cols="6">
                    <v-text-field
                        :value="activeUser.position"
                        @input="updateField($event, 'position')"
                        class="text-field-item mb-1"
                        dense
                        outlined
                    />
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col class="col-item row-title" cols="3">{{ "Role" | t }}</v-col>
                <v-col class="col-item" cols="6">
                    <v-select
                        :value="activeUser.role"
                        @input="updateField($event, 'role')"
                        placeholder="Select"
                        :items="roleItems"
                        class="text-field-item mb-1"
                        dense
                        outlined
                    />
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col class="col-item row-title" cols="3">{{ "Status" | t }}</v-col>
                <v-col class="col-item" cols="6">
                    <v-select
                        :value="activeUser.status"
                        @input="updateField($event, 'status')"
                        placeholder="Select"
                        :items="statusItems"
                        class="text-field-item mb-1"
                        dense
                        outlined
                    />
                </v-col>
            </v-row>
            <v-divider></v-divider>
            <v-row v-if="$route.params.userId != 'new'">
                <v-col cols="3">
                    <v-row v-for="(item, index) in activeUser.work_roles" :key="item.role">
                        <v-col class="col-item row-title" cols="6">{{ formatRoleName(item.role) }}</v-col>
                        <v-col class="col-item" cols="6">
                            <v-switch
                                class="pt-0 mt-0"
                                @change="updateWorkRole($event, 'value', index)"
                                v-model="item.value"
                                dense
                                color="primary"
                            />
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="3" v-if="activeUser.role === PermissionsConstants.ROLE_USER">
                    <v-row>
                        <v-col class="col-item row-title" cols="8">
                            <p class="pb-1">{{ formatRoleName(PermissionsConstants.ROLE_USER) }}</p>
                        </v-col>
                        <v-col class="col-item align-self-start" cols="4">
                            <v-btn
                                @click="updatePermissionFromRoleChange(PermissionsConstants.ROLE_USER, true)"
                                text
                                dense
                                color="bal-red"
                                class="btn-smaller"
                                >{{ "Apply" | t }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="3" v-if="activeUser.role === PermissionsConstants.ROLE_ADMIN">
                    <v-row v-for="(item, index) in adminRoles" :key="item.role">
                        <v-col class="col-item row-title" cols="8"
                            ><p class="pb-1">{{ formatRoleName(item) }}</p></v-col
                        >
                        <v-col class="col-item align-self-start" cols="4">
                            <v-btn
                                @click="updatePermissionFromRoleChange(item, true)"
                                text
                                dense
                                color="bal-red"
                                class="btn-smaller"
                                >{{ "Apply" | t }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
            <v-divider></v-divider>
            <user-permissions
                v-if="allActions.length > 0 && user.role == 'superadmin' && $route.params.userId != 'new'"
                :disabled="false"
                :editing="true"
            />
        </v-form>
    </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import UserPermissions from "@/components/user-permissions.vue";
import { PermissionsConstants } from "@/permissions";

export default {
    name: "profile-edit-tab",
    components: { "user-permissions": UserPermissions },

    data() {
        return {
            emailRules: [v => !!v || "E-mail is required", v => /.+@.+\..+/.test(v) || "E-mail must be valid"],
            passwordRules: [
                v => !!v || "Password is required",
                v => (v && v.length >= 8) || "Password must be at least 8 characters"
            ],
            valid: true,
            roleItems: ["admin", "user", "superadmin"],
            statusItems: ["active", "pending", "blocked", "inactive"]
        };
    },
    computed: {
        PermissionsConstants() {
            return PermissionsConstants;
        },
        ...mapState("UserStore", ["activeUser", "errorMessage", "allActions", "adminRoles"]),
        ...mapState(["user"]),
        permittedToChangePasswords() {
            return this.user.role === "superadmin" && !["admin", "superadmin"].includes(this.activeUser.role)
        }
    },
    mounted() {},
    methods: {
        ...mapMutations("UserStore", [
            "updateActiveUserWorkRole",
            "updateActiveUserProperty",
            "setDefaultRolePermission"
        ]),
        updateField(value, field) {
            this.updateActiveUserProperty({ prop: field, value: value });
        },
        updateWorkRole(value, field, index) {
            this.updateActiveUserWorkRole({ prop: field, value, index });
            this.updatePermissionFromRoleChange(this.activeUser?.work_roles[index].role, value);
        },
        updatePermissionFromRoleChange(role, change) {
            if (change) {
                this.setDefaultRolePermission(role);
            }
        },
        formatRoleName(name) {
            name = name.replaceAll("_", " ");

            return this.t(name.charAt(0).toUpperCase() + name.slice(1));
        }
    }
};
</script>

<style scoped>
.row-title {
    color: gray;
    margin-bottom: 0px;
}

.col-item {
    padding-top: 8px;
    padding-bottom: 8px;
}

.dot {
    margin-right: 10px;
    height: 15px;
    width: 15px;
    border-radius: 50%;
    display: inline-block;
}

.btn-smaller {
    height: 25px !important;
}
</style>
